import React, { useEffect } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

// Supported language codes
export const LANG_CODES = ['en', 'es', 'de', 'sl', 'sr'];
export const DEFAULT_LANG = 'en';

// Resolve a sensible language from localStorage or browser, falling back to default
export const getStoredOrDefaultLang = () => {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  const stored = localStorage.getItem('selectedLanguage');
  if (stored && LANG_CODES.includes(stored)) return stored;
  // Try browser language
  const browser = (navigator.language || '').slice(0, 2).toLowerCase();
  if (LANG_CODES.includes(browser)) return browser;
  return DEFAULT_LANG;
};

// Build a path with current language prefix (used by links/buttons)
export const withLang = (lang, subpath = '') => {
  const cleanSub = subpath.startsWith('/') ? subpath : `/${subpath}`;
  if (cleanSub === '/') return `/${lang}`;
  return `/${lang}${cleanSub}`;
};

// Replace (or insert) the language segment in the current pathname
export const swapLangInPath = (pathname, newLang) => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return `/${newLang}`;
  if (LANG_CODES.includes(segments[0])) {
    segments[0] = newLang;
  } else {
    segments.unshift(newLang);
  }
  return '/' + segments.join('/');
};

// Wraps language-prefixed routes: validates :lang param and syncs context
export const LanguageRouteSync = ({ children }) => {
  const { lang } = useParams();
  const { currentLanguage, changeLanguage } = useLanguage();
  const location = useLocation();

  const isValid = LANG_CODES.includes(lang);

  useEffect(() => {
    if (isValid && lang !== currentLanguage) {
      changeLanguage(lang);
    }
  }, [lang, isValid, currentLanguage, changeLanguage]);

  if (!isValid) {
    // Unknown lang segment — redirect to default-lang version of same path,
    // preserving any extra path segments after the bad lang code.
    const rest = location.pathname.split('/').slice(2).join('/');
    const target = rest ? `/${DEFAULT_LANG}/${rest}` : `/${DEFAULT_LANG}`;
    return <Navigate to={target + location.search + location.hash} replace />;
  }

  return children;
};

// Used at root "/" — redirects to stored/browser/default lang
export const RootRedirect = () => {
  const location = useLocation();
  const lang = getStoredOrDefaultLang();
  return <Navigate to={`/${lang}${location.search}${location.hash}`} replace />;
};

// Used for legacy unprefixed paths (/partners, /privacy, ...)
// Redirects to prefixed equivalent using stored/default lang
export const PrefixRedirect = ({ to }) => {
  const location = useLocation();
  const lang = getStoredOrDefaultLang();
  return <Navigate to={`/${lang}${to}${location.search}${location.hash}`} replace />;
};
